// src/oura/screens/Knowledge.tsx
//
// The Knowledge tab on a business (ourteam-frontend#3684).
//
// Everything a business told us that its website does not publish: a price
// list, a treatment menu, an owner's WhatsApp message about opening hours.
// Today none of that survives past the person who received it, and every fact
// Oura holds is either crawled or inferred — this is the third kind, and the
// only kind the client actually asserted.
//
// Staff-only for now. The client-facing upload card is a separate ticket.

import { useCallback, useEffect, useRef, useState } from "react";
import { BookOpenIcon, ClipboardIcon, Loader2Icon, UploadCloudIcon } from "lucide-react";
import {
    listKnowledge,
    uploadKnowledgeFile,
    addKnowledgeText,
    patchKnowledge,
    deleteKnowledge,
    KNOWLEDGE_CHAR_BUDGET,
    KNOWLEDGE_MAX_SOURCES,
    type AeoKnowledgeSource,
    type KnowledgeLimits,
} from "@service/aeo";
import { Card, CardBody, CardHeader } from "../primitives/Card";
import { Button } from "../primitives/Button";
import { EmptyState } from "../primitives/EmptyState";
import { useSnackbar } from "@hook/use-snackbar";
import { useWritesPaused } from "@context/maintenance";
import {
    KNOWLEDGE_ACCEPT,
    KnowledgeCountLine,
    KnowledgeDuplicateRow,
    KnowledgeNoteBox,
    KnowledgeRow,
    knowledgeCounts,
} from "../components/KnowledgeEntry";
import { KnowledgeReader } from "../components/KnowledgeReader";

/** How often to re-read while something is still being extracted. */
const POLL_MS = 2_000;

export function Knowledge({ businessId }: { businessId: string }) {
    const snackbar = useSnackbar();
    const writesPaused = useWritesPaused();
    const [sources, setSources] = useState<AeoKnowledgeSource[] | null>(null);
    /**
     * The caps, as the SERVER states them (ourteam-backend#4749 sends them on
     * every list read, expressly so the FE does not hold a second copy). The
     * constants below are the fallback for a deploy that predates the block —
     * a missing `limits` means unknown, never zero, or the budget line would
     * tell a healthy business it is full.
     */
    const [limits, setLimits] = useState<KnowledgeLimits | undefined>(undefined);
    const [busy, setBusy] = useState(false);
    const [noteOpen, setNoteOpen] = useState<{ seed?: string } | null>(null);
    const [duplicates, setDuplicates] = useState<string[]>([]);
    const [reading, setReading] = useState<AeoKnowledgeSource | null>(null);
    const fileRef = useRef<HTMLInputElement>(null);

    const refresh = useCallback(async () => {
        const page = await listKnowledge(businessId);
        setSources(page.sources);
        setLimits(page.limits);
    }, [businessId]);

    useEffect(() => { void refresh(); }, [refresh]);

    /**
     * Poll only while something is actually being read.
     *
     * The backend extracts inline under a 20s cap and finishes anything slower
     * on the worker, so `pending` is a real state with no event behind it. A
     * poll that ran forever would be a background request on a screen people
     * leave open all day; one that never ran would leave a row saying "Reading"
     * until someone reloaded — which the ticket names as the failure.
     */
    useEffect(() => {
        if (!sources?.some((s) => s.status === "pending")) return;
        const t = window.setInterval(() => { void refresh(); }, POLL_MS);
        return () => window.clearInterval(t);
    }, [sources, refresh]);

    const addFiles = async (files: File[]) => {
        setBusy(true);
        // One request per file, so three good files still land when the fourth
        // is a repeat — see uploadKnowledgeFile for why the endpoint's batch
        // form cannot give that.
        for (const file of files) {
            const r = await uploadKnowledgeFile(businessId, file);
            if (!r.ok && r.kind === "duplicate") setDuplicates((d) => [...d, r.title]);
            else if (!r.ok) snackbar.showError(`${r.title} could not be added`, r.message);
        }
        await refresh();
        setBusy(false);
    };

    const addNote = async (draft: { title: string; text: string; content_as_of: string }) => {
        const r = await addKnowledgeText(businessId, draft);
        if (!r.ok) {
            if (r.kind === "duplicate") setDuplicates((d) => [...d, r.title]);
            else snackbar.showError("That note could not be added", r.message);
            return false;
        }
        await refresh();
        return true;
    };

    const remove = async (id: string) => {
        if (await deleteKnowledge(businessId, id)) await refresh();
        else snackbar.showError("That source could not be removed");
    };

    const togglePark = async (s: AeoKnowledgeSource) => {
        const next = s.status === "excluded" ? "ready" : "excluded";
        if (await patchKnowledge(businessId, s.id, { status: next })) await refresh();
        else snackbar.showError("That source could not be changed");
    };

    const counts = knowledgeCounts(sources ?? []);
    const charBudget = limits?.maxTotalChars ?? KNOWLEDGE_CHAR_BUDGET;
    const maxSources = limits?.maxSources ?? KNOWLEDGE_MAX_SOURCES;
    const budgetPct = Math.min(100, Math.round((counts.chars / charBudget) * 100));
    // Nothing is read from a source that is parked or still failing, so the
    // line under the list counts what a scan would actually get.
    const usable = (sources ?? []).filter((s) => s.status === "ready").length;

    return (
        <div className="space-y-4">
            <input
                ref={fileRef}
                type="file"
                multiple
                disabled={writesPaused}
                accept={KNOWLEDGE_ACCEPT}
                className="hidden"
                onChange={(e) => {
                    const files = Array.from(e.target.files ?? []);
                    e.target.value = "";
                    if (files.length) void addFiles(files);
                }}
            />

            <Card>
                <CardHeader
                    icon={BookOpenIcon}
                    title="Knowledge"
                    description="What this business told us that its website does not say. Used by every scan, every question set and every draft from here on."
                    aside={
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" disabled={busy} writes onClick={() => setNoteOpen({})}>
                                <ClipboardIcon className="h-3.5 w-3.5" />Paste text
                            </Button>
                            <Button size="sm" writes disabled={busy} onClick={() => fileRef.current?.click()}>
                                {busy ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" /> : <UploadCloudIcon className="h-3.5 w-3.5" />}
                                Add files
                            </Button>
                        </div>
                    }
                />

                {(duplicates.length > 0 || noteOpen) && (
                    <CardBody className="space-y-2 border-t border-gray-100 pt-3">
                        {duplicates.map((title, i) => (
                            <KnowledgeDuplicateRow
                                key={`${title}-${i}`}
                                title={title}
                                onDismiss={() => setDuplicates((d) => d.filter((_, j) => j !== i))}
                            />
                        ))}
                        {noteOpen && (
                            <KnowledgeNoteBox
                                seedText={noteOpen.seed}
                                nextIndex={counts.notes + 1}
                                onAdd={addNote}
                                onDone={() => setNoteOpen(null)}
                            />
                        )}
                    </CardBody>
                )}

                {sources === null ? (
                    <div className="flex justify-center py-12">
                        <Loader2Icon className="h-5 w-5 animate-spin text-gray-400" />
                    </div>
                ) : sources.length === 0 ? (
                    <EmptyState
                        icon={BookOpenIcon}
                        title="Nothing here yet"
                        description="Add the things this business sent that its website does not publish — a price list, a menu, an email about opening hours. Every scan from here on reads them."
                    />
                ) : (
                    <div className="border-t border-gray-100">
                        {sources.map((s) => (
                            <KnowledgeRow key={s.id} source={s} onDelete={remove} onExclude={togglePark} onOpen={setReading} />
                        ))}
                    </div>
                )}

                {!!sources?.length && (
                    <div className="border-t border-gray-100 px-4 py-3">
                        <KnowledgeCountLine
                            sources={sources}
                            right={
                                <span className={usable > 0 ? "text-emerald-700" : "text-gray-400"}>
                                    {usable > 0 ? "Ready for the next scan" : "Nothing readable yet"}
                                </span>
                            }
                        />
                        <p className="my-0 mt-1 text-xs text-gray-400">
                            {counts.chars.toLocaleString()} characters · {budgetPct}% of the per run budget
                            {counts.total >= maxSources && ` · ${maxSources} source limit reached`}
                        </p>
                    </div>
                )}
            </Card>

            <KnowledgeReader businessId={businessId} source={reading} onClose={() => setReading(null)} />
        </div>
    );
}
